/* eslint-disable */
import React, { useLayoutEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { client } from "../../services/axiosClient";
import { useEffect, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import ChartistGraph from "react-chartist";
import { VectorMap } from "@react-jvectormap/core";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import AuthContext from "../../contexts/AuthProvider";
import { ThreeDots } from "react-loader-spinner";
import { getOom } from "../../utils/utils";
import useThemeColors from "../../hooks/useThemeColors";

import { arMill } from "@react-jvectormap/argentina";
import { brMill } from "@react-jvectormap/brazil";
import { usMill } from "@react-jvectormap/unitedstates";
import { worldMill } from "@react-jvectormap/world";
import { clMill } from "../../assets/maps/clMill";
import { alMill } from "../../assets/maps/alMill";
import { spStateMill } from "../../assets/maps/spStateMill";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Icon from "@material-ui/core/Icon";
import Hidden from "@material-ui/core/Hidden";

// @material-ui/icons
import Update from "@material-ui/icons/Update";
import AccessTime from "@material-ui/icons/AccessTime";
import Search from "@material-ui/icons/Search";
import Done from "@material-ui/icons/Done";

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Table from "../../components/Table/Table.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardBody from "../../components/Card/CardBody.js";
import CardFooter from "../../components/Card/CardFooter.js";

import InfoChanger from "../../components/InfoChanger/InfoChanger";

import { linePercentageChart, barChart } from "../../variables/charts";

import styles from "../../assets/jss/material-dashboard-pro-react/views/overviewStyle.js";
import tableStyles from "../../assets/jss/material-dashboard-pro-react/components/tableStyle";

const useStyles = makeStyles(styles);
const useTableStyles = makeStyles(tableStyles);

// Memoized components to prevent unnecessary re-renders
const MemoizedThreeDots = React.memo(ThreeDots);
const MemoizedChartistGraph = React.memo(ChartistGraph);
const MemoizedVectorMap = React.memo(VectorMap);

export default function Overview() {
  const classes = useStyles();
  const tableClasses = useTableStyles();
  const navigate = useNavigate();

  let { userDetails } = useContext(AuthContext);
  const { clientColor } = useThemeColors();

  const { t, i18n } = useTranslation();
  
  // Memoize the translate_months function
  const translate_months = useCallback((months_array) => {
    let months = [];
    for (let month of months_array) {
      months.push(t("months." + month));
    }
    return months;
  }, [t]);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [period, setPeriod] = useState({});
  const [volumeCard, setVolumeCard] = useState({
    name: t("overview.volume"),
    value: 0,
    unit: "",
    vs_ly: {
      value: "",
      icon: "",
      color: null,
    },
    vs_lm: {
      value: "",
      icon: "",
      color: null,
    },
    vs_ytd: {
      value: "",
      icon: "",
      color: null,
    },
  });
  const [netRevenueCard, setNetRevenueCard] = useState({
    name: t("overview.net_revenue"),
    value: 0,
    unit: "",
    vs_ly: {
      value: "",
      icon: "",
      color: null,
    },
    vs_lm: {
      value: "",
      icon: "",
      color: null,
    },
    vs_ytd: {
      value: "",
      icon: "",
      color: null,
    },
  });
  const [serveMarginCard, setServeMarginCard] = useState({
    name: t("overview.serve_margin"),
    value: "",
    vs_ly: {
      value: "",
      icon: "",
      color: null,
    },
    vs_lm: {
      value: "",
      icon: "",
      color: null,
    },
    vs_ytd: {
      value: "",
      icon: "",
      color: null,
    },
  });
  const [topChannelsTable, setTopChannelsTable] = useState([]);
  const [topCategoriesTable, setTopCategoriesTable] = useState([]);
  const [topRegionsTable, setTopRegionsTable] = useState([]);
  const [mapToDisplay, setMapToDisplay] = useState({
    map: arMill,
    strokeWidth: 1,
  });
  const [isShowMap, setIsShowMap] = useState(false);
  const [mapData, setMapData] = useState({});
  const [monthlyVolumeChart, setMonthlyVolumeChart] = useState({
    ...barChart,
    data: {
      labels: [],
      series: [[]],
    },
  });
  const [monthlyNetRevenueChart, setMonthlyNetRevenueChart] = useState({
    ...barChart,
    data: {
      labels: [],
      series: [[]],
    },
  });
  const [monthlyServeMarginChart, setMonthlyServeMarginChart] = useState({
    ...linePercentageChart,
    data: {
      labels: [],
      series: [[]],
    },
  });

  const [volumeOom, setVolumeOom] = useState({
    div: 1e6,
    x: " " + t("oom.m"),
  });
  const [revenueOom, setRevenueOom] = useState({
    div: 1e6,
    x: " " + t("oom.m"),
  });

  // Store graphMonths in state instead of global variable
  const [graphMonths, setGraphMonths] = useState([]);

  // Memoize the format_table_results function
  const format_table_results = useCallback((table_data) => {
    const formattedData = [...table_data];
    for (let i = 0; i < formattedData.length; i++) {
      formattedData[i][1] = (formattedData[i][1] * 100).toFixed(1) + "%";
    }
    return formattedData;
  }, []);

  useEffect(() => {
    client
      .post("api/overview/")
      .then((response) => {
        setPeriod({
          month: response.data.period.month,
          year: response.data.period.year,
          last_update: moment.utc(
            response.data.period.last_update,
            "YYYY-MM-DD HH:mm",
          ),
          next_update: response.data.period.next_update,
          latest_modeling_month: response.data.period.latest_modeling_month,
          months_pending_count: response.data.period.months_pending_count,
        });
        setVolumeCard({
          value: response.data.volume.value,
          unit: response.data.volume.unit,
          vs_ly: {
            valid: response.data.volume.vs_ly !== null,
            value: (100 * response.data.volume.vs_ly).toFixed(1),
            icon: response.data.volume.vs_ly > 0 ? "north" : "south",
            color:
              response.data.volume.vs_ly > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_lm: {
            valid: response.data.volume.vs_lm !== null,
            value: (100 * response.data.volume.vs_lm).toFixed(1),
            icon: response.data.volume.vs_lm > 0 ? "north" : "south",
            color:
              response.data.volume.vs_lm > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_ytd: {
            valid: response.data.volume.vs_ytd !== null,
            value: (100 * response.data.volume.vs_ytd).toFixed(1),
            icon: response.data.volume.vs_ytd > 0 ? "north" : "south",
            color:
              response.data.volume.vs_ytd > 0
                ? classes.successText
                : classes.dangerText,
          },
        });
        setNetRevenueCard({
          name: response.data.net_revenue.name,
          value: response.data.net_revenue.value,
          unit: response.data.net_revenue.unit,
          vs_ly: {
            valid: response.data.net_revenue.vs_ly !== null,
            value: (100 * response.data.net_revenue.vs_ly).toFixed(1),
            icon: response.data.net_revenue.vs_ly > 0 ? "north" : "south",
            color:
              response.data.net_revenue.vs_ly > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_lm: {
            valid: response.data.net_revenue.vs_lm !== null,
            value: (100 * response.data.net_revenue.vs_lm).toFixed(1),
            icon: response.data.net_revenue.vs_lm > 0 ? "north" : "south",
            color:
              response.data.net_revenue.vs_lm > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_ytd: {
            valid: response.data.net_revenue.vs_ytd !== null,
            value: (100 * response.data.net_revenue.vs_ytd).toFixed(1),
            icon: response.data.net_revenue.vs_ytd > 0 ? "north" : "south",
            color:
              response.data.net_revenue.vs_ytd > 0
                ? classes.successText
                : classes.dangerText,
          },
        });
        setServeMarginCard({
          name: response.data.serve_margin.name,
          value: (100 * response.data.serve_margin.value).toFixed(
            response.data.serve_margin.value < 0.1 ? 2 : 1,
          ),
          vs_ly: {
            valid: response.data.serve_margin.vs_ly !== null,
            value: (100 * response.data.serve_margin.vs_ly).toFixed(1),
            icon: response.data.serve_margin.vs_ly > 0 ? "north" : "south",
            color:
              response.data.serve_margin.vs_ly > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_lm: {
            valid: response.data.serve_margin.vs_lm !== null,
            value: (100 * response.data.serve_margin.vs_lm).toFixed(1),
            icon: response.data.serve_margin.vs_lm > 0 ? "north" : "south",
            color:
              response.data.serve_margin.vs_lm > 0
                ? classes.successText
                : classes.dangerText,
          },
          vs_ytd: {
            valid: response.data.serve_margin.vs_ytd !== null,
            value: (100 * response.data.serve_margin.vs_ytd).toFixed(1),
            icon: response.data.serve_margin.vs_ytd > 0 ? "north" : "south",
            color:
              response.data.serve_margin.vs_ytd > 0
                ? classes.successText
                : classes.dangerText,
          },
        });
        setTopChannelsTable(format_table_results(response.data.top_channels));
        setTopCategoriesTable(
          format_table_results(response.data.top_categories),
        );
        setTopRegionsTable(format_table_results(response.data.top_regions));
        setMapToDisplay(() => {
          switch (response.data.map_to_display) {
            case "ar":
              return { map: arMill, strokeWidth: 1 };
            case "br":
              return { map: brMill, strokeWidth: 300 };
            case "cl":
              return { map: clMill, strokeWidth: 1 };
            case "us":
              return { map: usMill, strokeWidth: 1 };
            case "sp":
              return { map: spStateMill, strokeWidth: 1 };
            case "al":
              return { map: alMill, strokeWidth: 1 };
            case "wd":
              return { map: worldMill, strokeWidth: 1 };
            default:
              return { map: arMill, strokeWidth: 1 };
          }
        });
        setMapData(response.data.map_data);
        setIsShowMap(false);
        setTimeout(() => {
          setIsShowMap(true);
        });
        setGraphMonths(response.data.monthly_results.months);
        let translatedMonths = translate_months(response.data.monthly_results.months);
        setMonthlyVolumeChart({
          ...barChart,
          data: {
            labels: translatedMonths,
            series: [response.data.monthly_results.volume],
          },
        });
        setMonthlyNetRevenueChart({
          ...barChart,
          data: {
            labels: translatedMonths,
            series: [response.data.monthly_results.net_revenue],
          },
        });
        setMonthlyServeMarginChart({
          ...linePercentageChart,
          data: {
            labels: translatedMonths,
            series: [response.data.monthly_results.serve_margin],
          },
        });
        setDataLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [classes.successText, classes.dangerText, format_table_results, translate_months]);

  const hasValidData = useMemo(() => {
    return dataLoaded && period.year && period.month;
  }, [dataLoaded, period.year, period.month]);

  useEffect(() => {
    moment.locale(i18n.language);
    let translatedMonths = translate_months(graphMonths);
    setMonthlyVolumeChart(prev => ({
      ...prev,
      data: {
        ...prev.data,
        labels: translatedMonths,
      },
    }));
    setMonthlyNetRevenueChart(prev => ({
      ...prev,
      data: {
        ...prev.data,
        labels: translatedMonths,
      },
    }));
    setMonthlyServeMarginChart(prev => ({
      ...prev,
      data: {
        ...prev.data,
        labels: translatedMonths,
      },
    }));
  }, [i18n.language, graphMonths, translate_months]);

  useLayoutEffect(() => {
    let val = volumeCard.value;
    let { div, x } = getOom(val);
    x = x === "" ? x : " " + t("oom." + x);
    setVolumeOom({ div: div, x: x });
  }, [volumeCard.value, t]);

  useLayoutEffect(() => {
    let val = netRevenueCard.value;
    let { div, x } = getOom(val);
    x = x === "" ? x : " " + t("oom." + x);
    setRevenueOom({ div: div, x: x });
  }, [netRevenueCard.value, t]);

  // Memoize chart data calculations
  const getAdjustedVolumeData = useMemo(() => {
    let data = structuredClone(monthlyVolumeChart.data);
    let series = [];
    monthlyVolumeChart.data.series[0].forEach((val) => {
      series.push(val / volumeOom.div);
    });
    data = {
      ...data,
      series: [series],
    };
    return data;
  }, [monthlyVolumeChart.data, volumeOom.div]);

  const getAdjustedRevenueData = useMemo(() => {
    let data = structuredClone(monthlyNetRevenueChart.data);
    let series = [];
    monthlyNetRevenueChart.data.series[0].forEach((val) => {
      series.push(val / revenueOom.div);
    });
    data = {
      ...data,
      series: [series],
    };
    return data;
  }, [monthlyNetRevenueChart.data, revenueOom.div]);

  // Memoize spinner components
  const cardSpinner = useMemo(() => {
    if (!dataLoaded)
      return (
        <>
          <div className={classes.cardSpinner}>
            <MemoizedThreeDots
              color={clientColor}
              ariaLabel="loading"
              wrapperStyle={{ marginTop: "5px", marginBottom: "16px" }}
              wrapperClass=""
            />
          </div>
        </>
      );
    if (dataLoaded && !hasValidData)
      return (
        <>
          <div className={classes.cardSpinner}>
            <p style={{ color: "#999", fontSize: "14px", margin: "16px 0" }}>
              {t("overview.no_data")}
            </p>
          </div>
        </>
      );
    return null;
  }, [dataLoaded, hasValidData, classes.cardSpinner, clientColor, t]);

  const tableSpinner = useMemo(() => {
    if (!dataLoaded)
      return (
        <>
          <div className={classes.tableSpinner}>
            <MemoizedThreeDots
              width="100px"
              height="100px"
              color={clientColor}
              ariaLabel="loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        </>
      );
    if (dataLoaded && !hasValidData)
      return (
        <>
          <div className={classes.tableSpinner}>
            <p style={{ color: "#999", fontSize: "16px" }}>
              {t("overview.no_data")}
            </p>
          </div>
        </>
      );
    return null;
  }, [dataLoaded, hasValidData, classes.tableSpinner, clientColor, t]);

  // Memoize card footer date
  const cardFooterDate = useMemo(() => {
    let content;
    if (dataLoaded && hasValidData) {
      content = (
        <>
          {t("months." + period.month)}
          {"-"}
          {period.year.slice(-2)}
        </>
      );
    } else {
      content = <div style={{ height: "23px" }}></div>;
    }
    return (
      <GridItem xs={3} container justifyContent="flex-end">
        <div className={classes.stats}>{content}</div>
      </GridItem>
    );
  }, [dataLoaded, hasValidData, period.month, period.year, t, classes.stats]);

  // Memoize info changer data
  const getInfoChangerData = useCallback((card, symbol) => {
    let data = [];
    if (card.vs_lm.valid) {
      data.push(
        <div key={1}>
          <span className={card.vs_lm.color}>
            <Icon>{card.vs_lm.icon}</Icon>
            {card.vs_lm.value + symbol}
          </span>
          &nbsp;
          {t("analysis.vs_lm")}
        </div>,
      );
    }
    if (card.vs_ly.valid) {
      data.push(
        <div key={2}>
          <span className={card.vs_ly.color}>
            <Icon>{card.vs_ly.icon}</Icon>
            {card.vs_ly.value + symbol}
          </span>
          &nbsp;
          {t("analysis.vs_ly")}
        </div>,
      );
    }
    if (card.vs_ytd.valid) {
      data.push(
        <div key={3}>
          <span className={card.vs_ytd.color}>
            <Icon>{card.vs_ytd.icon}</Icon>
            {card.vs_ytd.value + symbol}
          </span>
          &nbsp;
          {t("analysis.vs_ytd")}
        </div>,
      );
    }
    return data;
  }, [t]);

  // Memoize navigation handler
  const handleNavigateToMonthlyPL = useCallback(() => {
    navigate("/main/monthly-pl");
  }, [navigate]);

  // Memoize map region tip handler
  const handleRegionTipShow = useCallback((e, el, code) => {
    el.html(
      `${el.html()} | ${(100 * mapData[code]).toFixed(1)}%`,
    );
  }, [mapData]);

  // Memoize map series configuration
  const mapSeries = useMemo(() => ({
    regions: [
      {
        values: mapData,
        scale: [
          "#F8696B",
          "#FBAA77",
          "#FFEB84",
          "#B1D580",
          "#63BE7B",
        ],
      },
    ],
  }), [mapData]);

  // Memoize map region style
  const mapRegionStyle = useMemo(() => ({
    initial: {
      fill: "#e4e4e4",
      "fill-opacity": 0.8,
      stroke: "white",
      "stroke-width": mapToDisplay.strokeWidth,
      "stroke-opacity": 1,
    },
  }), [mapToDisplay.strokeWidth]);

  return (
    <>
      <div>
        <GridContainer
          className={classes.cardsWrapper}
          style={{ marginTop: "-40px" }}
        >
          <GridItem xs={12} sm={6} md={6} lg={3}>
            <Card>
              <CardHeader color="client" stats icon>
                <CardIcon color="client">
                  <Icon>local_shipping</Icon>
                </CardIcon>
                <p className={classes.cardCategory}>
                  {dataLoaded && hasValidData && t("overview.volume")}
                </p>
                {dataLoaded && hasValidData && (
                  <h3 className={classes.cardTitle}>
                    {(volumeCard.value / volumeOom.div).toFixed(
                      volumeCard.value / volumeOom.div < 10 ? 2 : 1,
                    ) + volumeOom.x}{" "}
                    <small>{volumeCard.unit}</small>
                  </h3>
                )}
                {cardSpinner}
              </CardHeader>
              <CardFooter stats>
                <GridContainer>
                  {dataLoaded && hasValidData && (
                    <GridItem xs={9}>
                      <div className={classes.stats}>
                        <InfoChanger
                          info={getInfoChangerData(volumeCard, "%")}
                          interval={5000}
                        />
                      </div>
                    </GridItem>
                  )}
                  {cardFooterDate}
                </GridContainer>
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={3}>
            <Card>
              <CardHeader color="client" stats icon>
                <CardIcon color="client">
                  <Icon>monetization_on</Icon>
                </CardIcon>
                <p className={classes.cardCategory}>
                  {dataLoaded && hasValidData && netRevenueCard.name}
                </p>
                {dataLoaded && hasValidData && (
                  <h3 className={classes.cardTitle}>
                    {(netRevenueCard.value / revenueOom.div).toFixed(
                      netRevenueCard.value / revenueOom.div < 10 ? 2 : 1,
                    )}{" "}
                    <small>{netRevenueCard.unit + revenueOom.x}</small>
                  </h3>
                )}
                {cardSpinner}
              </CardHeader>
              <CardFooter stats>
                <GridContainer>
                  {dataLoaded && hasValidData && (
                    <GridItem xs={9}>
                      <div className={classes.stats}>
                        <InfoChanger
                          info={getInfoChangerData(netRevenueCard, "%")}
                          interval={5000}
                        />
                      </div>
                    </GridItem>
                  )}
                  {cardFooterDate}
                </GridContainer>
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={3}>
            <Card>
              <CardHeader color="client" stats icon>
                <CardIcon color="client">
                  <Icon>account_balance_wallet</Icon>
                </CardIcon>
                <p className={classes.cardCategory}>
                  {dataLoaded && hasValidData && serveMarginCard.name}
                </p>
                {dataLoaded && hasValidData && (
                  <h3 className={classes.cardTitle}>
                    {serveMarginCard.value} <small>%</small>
                  </h3>
                )}
                {cardSpinner}
              </CardHeader>
              <CardFooter stats>
                <GridContainer>
                  {dataLoaded && hasValidData && (
                    <GridItem xs={9}>
                      <div className={classes.stats}>
                        <InfoChanger
                          info={getInfoChangerData(serveMarginCard, " pp")}
                          interval={5000}
                        />
                      </div>
                    </GridItem>
                  )}
                  {cardFooterDate}
                </GridContainer>
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={3}>
            <Card>
              <CardHeader color="client" stats icon>
                <CardIcon color="client">
                  <Icon>alarm_on</Icon>
                </CardIcon>
                <p className={classes.cardCategory}>
                  {dataLoaded && hasValidData && t("overview.last_update")}
                </p>
                {dataLoaded && hasValidData && period.last_update && (
                  <h3 className={classes.cardTitle}>
                    {period.last_update.local().format("L")}
                  </h3>
                )}
                {cardSpinner}
              </CardHeader>
              <CardFooter stats>
                {dataLoaded && hasValidData && (
                  <div className={classes.stats}>
                    {period.months_pending_count === 0 && (
                      <span className={classes.successColor}>
                        <Done />
                        {t("overview.no_pending_updates")}
                      </span>
                    )}
                    {period.months_pending_count === 1 &&
                      period.latest_modeling_month &&
                      period.latest_modeling_month[0] && (
                        <span className={classes.warningColor}>
                          <Update />
                          {t("overview.update_pending1")}{" "}
                          {t("months." + period.latest_modeling_month[1])}
                          {"-"}
                          {period.latest_modeling_month[0]
                            .toString()
                            .slice(-2)}{" "}
                          {t("overview.update_pending2")}
                        </span>
                      )}
                    {period.months_pending_count > 1 && (
                      <span className={classes.warningColor}>
                        <Update />
                        {period.months_pending_count}{" "}
                        {t("overview.months_with_pending_calculations")}
                      </span>
                    )}
                  </div>
                )}
                {!dataLoaded && <div style={{ height: "23px" }}></div>}
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
        <GridContainer style={{ marginTop: "-34px" }}>
          <GridItem xs={12}>
            <Card>
              <CardHeader color="client" icon>
                <h4 className={classes.cardIconTitle}>
                  {t("overview.serve_margin_top_channels_categories_locations")}
                  {dataLoaded && hasValidData && (
                    <small>
                      {" - "}
                      {t("months." + period.month)}
                      {"-"}
                      {period.year.slice(-2)}
                    </small>
                  )}
                </h4>
              </CardHeader>
              <CardBody style={{ minHeight: "318px" }}>
                <GridContainer justifyContent="space-between">
                  <GridItem xs={12} sm={12} md={12} lg={3}>
                    <GridContainer>
                      <GridItem xs={12}>
                        <Table
                          hover
                          customCellClasses={[tableClasses.right]}
                          customClassesForCells={[1]}
                          tableData={topChannelsTable}
                        />
                      </GridItem>
                      <Hidden lgUp>
                        <GridItem xs={12}>
                          <div style={{ height: "16px" }}></div>
                        </GridItem>
                      </Hidden>
                    </GridContainer>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={12} lg={3}>
                    <GridContainer>
                      <GridItem xs={12}>
                        <Table
                          hover
                          customCellClasses={[tableClasses.right]}
                          customClassesForCells={[1]}
                          tableData={topCategoriesTable}
                        />
                      </GridItem>
                      <Hidden lgUp>
                        <GridItem xs={12}>
                          <div style={{ height: "16px" }}></div>
                        </GridItem>
                      </Hidden>
                    </GridContainer>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={12} lg={3}>
                    <GridContainer>
                      <GridItem xs={12}>
                        <Table
                          hover
                          customCellClasses={[tableClasses.right]}
                          customClassesForCells={[1]}
                          tableData={topRegionsTable}
                        />
                      </GridItem>
                      <Hidden lgUp>
                        <GridItem xs={12}>
                          <div style={{ height: "16px" }}></div>
                        </GridItem>
                      </Hidden>
                    </GridContainer>
                  </GridItem>
                  <GridItem xs={false} sm={12} md={12} lg={3}>
                    <div
                      style={{
                        width: "100%",
                        height: "280px",
                      }}
                    >
                      {isShowMap && (
                        <MemoizedVectorMap
                          map={mapToDisplay.map}
                          backgroundColor="transparent"
                          zoomOnScroll={false}
                          containerStyle={{
                            width: "100%",
                            height: "100%",
                          }}
                          containerClassName="map"
                          regionStyle={mapRegionStyle}
                          series={mapSeries}
                          onRegionTipShow={handleRegionTipShow}
                        />
                      )}
                    </div>
                  </GridItem>
                  {tableSpinner}
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
        <GridContainer style={{ marginTop: "-16px" }}>
          <GridItem xs={12} sm={12} md={4}>
            <Card chart className={classes.cardHover}>
              <CardHeader
                color={
                  userDetails.implementation_tone === "color"
                    ? "info"
                    : userDetails.implementation_tone === "gray"
                      ? "gray"
                      : "client"
                }
                className={classes.cardHeaderHover}
              >
                <MemoizedChartistGraph
                  className="ct-chart-white-colors"
                  data={getAdjustedVolumeData}
                  type="Bar"
                  options={monthlyVolumeChart.options}
                  listener={monthlyVolumeChart.animation}
                />
              </CardHeader>
              <CardBody>
                <div className={classes.cardHoverUnder}>
                  <Tooltip
                    id="tooltip-top"
                    title={t("overview.view_details")}
                    placement="bottom"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <Button
                      simple
                      color="client"
                      justIcon
                      onClick={handleNavigateToMonthlyPL}
                    >
                      <Search className={classes.underChartIcons} />
                    </Button>
                  </Tooltip>
                </div>
                <h4 className={classes.cardTitle}>
                  {dataLoaded && hasValidData && t("overview.volume")}
                  {!dataLoaded && (
                    <MemoizedThreeDots
                      color={clientColor}
                      width={"50px"}
                      height={"50px"}
                      wrapperStyle={{
                        marginTop: "-15px",
                        marginBottom: "-10px",
                      }}
                      ariaLabel="loading"
                    />
                  )}
                  {dataLoaded && !hasValidData && (
                    <span style={{ color: "#999" }}>{t("overview.no_data")}</span>
                  )}
                </h4>
                <p className={classes.cardCategory}>
                  {(volumeOom.x === "" ? "" : volumeOom.x + " ") +
                    volumeCard.unit}
                  &nbsp;&#x2502;&nbsp;
                  {t("overview.last_12_months")}
                </p>
              </CardBody>
              <CardFooter chart>
                {dataLoaded && hasValidData && period.last_update && (
                  <div className={classes.stats}>
                    <AccessTime />
                    {t("overview.updated")}{" "}
                    {period.last_update.local().fromNow()}
                  </div>
                )}
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={12} md={4}>
            <Card chart className={classes.cardHover}>
              <CardHeader
                color={
                  userDetails.implementation_tone === "color"
                    ? "warning"
                    : userDetails.implementation_tone === "gray"
                      ? "gray"
                      : "client"
                }
                className={classes.cardHeaderHover}
              >
                <MemoizedChartistGraph
                  className="ct-chart-white-colors"
                  data={getAdjustedRevenueData}
                  type="Bar"
                  options={monthlyNetRevenueChart.options}
                  responsiveOptions={monthlyNetRevenueChart.responsiveOptions}
                  listener={monthlyNetRevenueChart.animation}
                />
              </CardHeader>
              <CardBody>
                <div className={classes.cardHoverUnder}>
                  <Tooltip
                    id="tooltip-top"
                    title={t("overview.view_details")}
                    placement="bottom"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <Button
                      simple
                      color="client"
                      justIcon
                      onClick={handleNavigateToMonthlyPL}
                    >
                      <Search className={classes.underChartIcons} />
                    </Button>
                  </Tooltip>
                </div>
                <h4 className={classes.cardTitle}>
                  {dataLoaded && hasValidData && netRevenueCard.name}
                  {!dataLoaded && (
                    <MemoizedThreeDots
                      color={clientColor}
                      width={"50px"}
                      height={"50px"}
                      wrapperStyle={{
                        marginTop: "-15px",
                        marginBottom: "-10px",
                      }}
                      ariaLabel="loading"
                    />
                  )}
                  {dataLoaded && !hasValidData && (
                    <span style={{ color: "#999" }}>{t("overview.no_data")}</span>
                  )}
                </h4>
                <p className={classes.cardCategory}>
                  {netRevenueCard.unit + revenueOom.x}
                  &nbsp;&#x2502;&nbsp;
                  {t("overview.last_12_months")}
                </p>
              </CardBody>
              <CardFooter chart>
                {dataLoaded && hasValidData && period.last_update && (
                  <div className={classes.stats}>
                    <AccessTime />
                    {t("overview.updated")}{" "}
                    {period.last_update.local().fromNow()}
                  </div>
                )}
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={12} md={4}>
            <Card chart className={classes.cardHover}>
              <CardHeader
                color={
                  userDetails.implementation_tone === "color"
                    ? "danger"
                    : userDetails.implementation_tone === "gray"
                      ? "gray"
                      : "client"
                }
                className={classes.cardHeaderHover}
              >
                <MemoizedChartistGraph
                  className="ct-chart-white-colors"
                  data={monthlyServeMarginChart.data}
                  type="Line"
                  options={monthlyServeMarginChart.options}
                  listener={monthlyServeMarginChart.animation}
                />
              </CardHeader>
              <CardBody>
                <div className={classes.cardHoverUnder}>
                  <Tooltip
                    id="tooltip-top"
                    title={t("overview.view_details")}
                    placement="bottom"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <Button
                      simple
                      color="client"
                      justIcon
                      onClick={handleNavigateToMonthlyPL}
                    >
                      <Search className={classes.underChartIcons} />
                    </Button>
                  </Tooltip>
                </div>
                <h4 className={classes.cardTitle}>
                  {dataLoaded && hasValidData && serveMarginCard.name}
                  {!dataLoaded && (
                    <MemoizedThreeDots
                      color={clientColor}
                      width={"50px"}
                      height={"50px"}
                      wrapperStyle={{
                        marginTop: "-15px",
                        marginBottom: "-10px",
                      }}
                      ariaLabel="loading"
                    />
                  )}
                  {dataLoaded && !hasValidData && (
                    <span style={{ color: "#999" }}>{t("overview.no_data")}</span>
                  )}
                </h4>
                <p className={classes.cardCategory}>
                  {t("overview.last_12_months")}
                </p>
              </CardBody>
              <CardFooter chart>
                {dataLoaded && hasValidData && period.last_update && (
                  <div className={classes.stats}>
                    <AccessTime />
                    {t("overview.updated")}{" "}
                    {period.last_update.local().fromNow()}
                  </div>
                )}
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    </>
  );
}
